import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, FileText, TrendingUp, Package, CheckCircle,
  RefreshCw, DollarSign, Users, Star, Copy, Wand2, Globe,
  Filter, Search, ChevronDown, ChevronUp, Loader2, Target
} from 'lucide-react';
import RevenueProjections from './RevenueProjections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  
  const [aiForm, setAiForm] = useState({
    practice_specialty: '',
    practice_location: '',
    competitor_urls: '',
    target_market: 'mid-range'
  });

  useEffect(() => {
    loadTemplates();
    loadCustomTemplates();
  }, [filterType, filterTier]);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/membership-templates/predefined`;
      
      const params = [];
      if (filterType !== 'all') params.push(`practice_type=${filterType}`);
      if (filterTier !== 'all') params.push(`tier=${filterTier}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
      setLoading(false);
    }
  };

  const loadCustomTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/membership-templates/custom`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomTemplates(response.data);
    } catch (error) {
      console.error('Error loading custom templates:', error);
    }
  };

  const handleAIRequest = async (e) => {
    e.preventDefault();
    setAiLoading(true);

    try {
      const token = localStorage.getItem('token');
      const competitorUrls = aiForm.competitor_urls
        .split('\n')
        .map(url => url.trim())
        .filter(url => url);

      const response = await axios.post(
        `${API_URL}/api/membership-templates/ai-suggestions`,
        {
          practice_specialty: aiForm.practice_specialty,
          practice_location: aiForm.practice_location || null,
          competitor_urls: competitorUrls,
          target_market: aiForm.target_market
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAiSuggestions(response.data);
      toast.success('AI suggestions generated successfully!');
    } catch (error) {
      console.error('AI Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const useTemplate = async (template) => {
    try {
      // Store template in local storage for the plans page to use
      localStorage.setItem('selectedTemplate', JSON.stringify(template));
      toast.success('Template selected! Creating plans...');
      
      // Create plans immediately instead of relying on Plans page
      const token = localStorage.getItem('token');
      const plansToCreate = template.plans || [];
      
      const createPromises = plansToCreate.map(async (plan) => {
        // Map billing_cycle to duration_months
        const durationMap = { monthly: 1, quarterly: 3, annual: 12 };
        const billingCycle = plan.billing_cycle || 'monthly';
        const duration_months = durationMap[billingCycle] || 1;

        const payload = {
          name: plan.name || 'Unnamed Plan',
          description: plan.description || '',
          price: parseFloat(plan.price || 0),
          duration_months: duration_months,
          benefits: plan.features || []
        };

        return axios.post(`${API_URL}/api/plans`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      });

      await Promise.all(createPromises);
      
      toast.success(`Successfully created ${plansToCreate.length} plan${plansToCreate.length > 1 ? 's' : ''} from template!`);
      
      // Clear the template from localStorage
      localStorage.removeItem('selectedTemplate');
      
      // Redirect to plans page
      setTimeout(() => {
        window.location.href = '/admin/plans';
      }, 1500);
    } catch (error) {
      console.error('Error creating plans from template:', error);
      toast.error(error.response?.data?.detail || 'Failed to create plans from template');
    }
  };

  const useAISuggestion = (suggestion) => {
    const templateData = {
      name: suggestion.plan_name,
      ai_generated: true,
      ...suggestion
    };
    localStorage.setItem('selectedTemplate', JSON.stringify(templateData));
    toast.success('AI suggestion loaded! Redirecting to Plans page...');
    setTimeout(() => {
      window.location.href = '/admin/plans';
    }, 1500);
  };

  const getTierBadge = (tier) => {
    const config = {
      basic: { color: 'bg-gray-100 text-gray-700', label: 'Basic' },
      standard: { color: 'bg-blue-100 text-blue-700', label: 'Standard' },
      premium: { color: 'bg-purple-100 text-purple-700', label: 'Premium' },
      tiered: { color: 'bg-gradient-to-r from-blue-500 to-purple-500 text-white', label: 'Tiered' }
    };
    const c = config[tier] || config.basic;
    return <Badge className={`${c.color} border-0`}>{c.label}</Badge>;
  };

  const getPracticeIcon = (practiceType) => {
    const icons = {
      healthcare: '🏥',
      aesthetic: '💅',
      dental: '🦷',
      wellness: '🌿',
      medspa: '💆'
    };
    return icons[practiceType] || '📋';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Membership Templates</h2>
          <p className="text-gray-600">Choose a template or get AI-powered suggestions</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={loadTemplates} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAIModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            AI Suggestions
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Practice Type</Label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Types</option>
                <option value="healthcare">Healthcare</option>
                <option value="aesthetic">Aesthetic</option>
                <option value="dental">Dental</option>
                <option value="wellness">Wellness</option>
                <option value="medspa">MedSpa</option>
              </select>
            </div>
            <div>
              <Label>Tier</Label>
              <select
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="tiered">Tiered (Multiple Plans)</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Gallery */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start mb-2">
                <div className="text-3xl">{getPracticeIcon(template.practice_type)}</div>
                {getTierBadge(template.tier)}
              </div>
              <CardTitle className="text-xl">{template.name}</CardTitle>
              <CardDescription>{template.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {template.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              <div className="text-sm text-gray-600">
                <strong>{template.plans.length}</strong> plan{template.plans.length > 1 ? 's' : ''} included
              </div>

              {/* Preview first plan */}
              {template.plans[0] && (
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{template.plans[0].name}</span>
                    <span className="text-teal-600 font-bold">
                      ${template.plans[0].price}/{template.plans[0].billing_cycle}
                    </span>
                  </div>
                  <ul className="text-xs space-y-1">
                    {template.plans[0].features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        {feature}
                      </li>
                    ))}
                    {template.plans[0].features.length > 3 && (
                      <li className="text-gray-500">+{template.plans[0].features.length - 3} more...</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  {expandedTemplate === template.id ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      View Details
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => useTemplate(template)}
                  size="sm"
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Use Template
                </Button>
              </div>

              {/* Expanded Details */}
              {expandedTemplate === template.id && (
                <div className="mt-4 space-y-3 border-t pt-4">
                  {template.plans.map((plan, idx) => (
                    <div key={idx} className="bg-white border rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold">{plan.name}</h4>
                        <span className="text-teal-600 font-bold">
                          ${plan.price}/{plan.billing_cycle}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-700">Features:</p>
                        {plan.features.map((feature, fIdx) => (
                          <div key={fIdx} className="flex items-start gap-2 text-xs">
                            <CheckCircle className="h-3 w-3 text-green-500 mt-0.5" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Suggestions Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-6 w-6 text-purple-600" />
                AI-Powered Membership Suggestions
              </CardTitle>
              <CardDescription>
                Get personalized membership plan recommendations based on your practice and market research
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!aiSuggestions ? (
                <form onSubmit={handleAIRequest} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Practice Specialty *</Label>
                    <Input
                      required
                      value={aiForm.practice_specialty}
                      onChange={(e) => setAiForm({ ...aiForm, practice_specialty: e.target.value })}
                      placeholder="e.g., Plastic Surgery, Primary Care, Anti-Aging"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={aiForm.practice_location}
                      onChange={(e) => setAiForm({ ...aiForm, practice_location: e.target.value })}
                      placeholder="e.g., Beverly Hills, CA"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Competitor URLs (one per line)</Label>
                    <Textarea
                      value={aiForm.competitor_urls}
                      onChange={(e) => setAiForm({ ...aiForm, competitor_urls: e.target.value })}
                      placeholder="https://competitor1.com&#10;https://competitor2.com"
                      rows={4}
                    />
                    <p className="text-xs text-gray-500">AI will analyze these websites for insights</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Target Market *</Label>
                    <select
                      required
                      value={aiForm.target_market}
                      onChange={(e) => setAiForm({ ...aiForm, target_market: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="budget">Budget-Friendly</option>
                      <option value="mid-range">Mid-Range</option>
                      <option value="premium">Premium/Luxury</option>
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={aiLoading}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    >
                      {aiLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          Generate Suggestions
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAIModal(false);
                        setAiSuggestions(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  {/* Fallback Mode Notice */}
                  {aiSuggestions.research_context?.fallback_mode && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-300">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-amber-600" />
                        <h3 className="font-semibold text-amber-900">Using Industry Templates</h3>
                      </div>
                      <p className="text-sm text-amber-700">
                        {aiSuggestions.notice || aiSuggestions.research_context.fallback_reason}
                      </p>
                      <p className="text-xs text-amber-600 mt-2">
                        💡 Tip: Add balance to your Universal Key in Profile → Universal Key for AI-powered personalized suggestions.
                      </p>
                    </div>
                  )}

                  {/* AI Success Message */}
                  {!aiSuggestions.research_context?.fallback_mode && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                        <h3 className="font-semibold text-purple-900">AI Analysis Complete</h3>
                      </div>
                      <p className="text-sm text-purple-700">
                        Generated {aiSuggestions.suggestions.length} personalized membership plans
                        {aiSuggestions.research_context?.competitors_analyzed > 0 && 
                          ` based on analysis of ${aiSuggestions.research_context.competitors_analyzed} competitor(s)`}
                        {aiSuggestions.research_context?.rag_enhanced && 
                          ` enhanced with data from similar practices`}
                      </p>
                      <p className="text-sm text-purple-600 font-medium mt-2">
                        Review the plans below and click "Save All Plans" to add them to your membership offerings.
                      </p>
                    </div>
                  )}

                  <div className="grid gap-4">
                    {aiSuggestions.suggestions.map((suggestion, idx) => (
                      <Card key={idx} className="border-2 border-purple-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="flex items-center gap-2">
                                {suggestion.plan_name}
                                {getTierBadge(suggestion.tier)}
                              </CardTitle>
                              <p className="text-sm text-gray-600 mt-1">{suggestion.target_profile}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-teal-600">
                                ${suggestion.monthly_price}
                              </div>
                              <div className="text-xs text-gray-500">per month</div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-700 italic">{suggestion.marketing_description}</p>
                          </div>

                          <div>
                            <p className="text-sm font-semibold text-gray-700 mb-2">Key Features:</p>
                            <div className="grid gap-2">
                              {suggestion.features.map((feature, fIdx) => (
                                <div key={fIdx} className="flex items-start gap-2 text-sm">
                                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {suggestion.service_inclusions && suggestion.service_inclusions.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-2">Service Inclusions:</p>
                              <div className="grid gap-2">
                                {suggestion.service_inclusions.map((service, sIdx) => (
                                  <div key={sIdx} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                                    <span>{service.quantity} {service.service}</span>
                                    <span className="text-teal-600 font-medium">{service.estimated_value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="bg-purple-50 p-3 rounded-lg">
                            <p className="text-xs font-semibold text-purple-900 mb-1">
                              <Target className="h-3 w-3 inline mr-1" />
                              Competitive Advantage:
                            </p>
                            <p className="text-sm text-purple-700">{suggestion.competitive_advantage}</p>
                          </div>

                          {/* Revenue Projections Component */}
                          <RevenueProjections suggestion={suggestion} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={async () => {
                        // Save all AI suggestions as plans
                        if (!window.confirm('Save all 3 AI-generated plans to your Membership Plans?')) return;
                        
                        try {
                          const token = localStorage.getItem('token');
                          const promises = aiSuggestions.suggestions.map(async (suggestion) => {
                            const planData = {
                              name: suggestion.plan_name,
                              price: suggestion.monthly_price,
                              duration_months: 1,  // Monthly by default
                              description: suggestion.marketing_description || '',
                              benefits: suggestion.features || []
                            };
                            
                            return axios.post(`${API_URL}/api/plans`, planData, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                          });
                          
                          await Promise.all(promises);
                          toast.success(`Successfully saved ${aiSuggestions.suggestions.length} plans!`);
                          setShowAIModal(false);
                          setAiSuggestions(null);
                          
                          // Redirect to plans page
                          setTimeout(() => {
                            window.location.href = '/admin/plans';
                          }, 1500);
                        } catch (error) {
                          toast.error('Failed to save plans');
                        }
                      }}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Save All Plans
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setAiSuggestions(null)}
                    >
                      Generate New
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAIModal(false);
                        setAiSuggestions(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MembershipTemplates;
